import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSession } from './lib/session';
import { countAwaitingDecision, queueCounts, type Queue } from './lib/api';
import type { Member } from './types';
import { LOCALES } from './lib/locales';
import { useBatch } from './lib/batch';
import { navigate, useRoute } from './lib/route';
import Focus from './views/Focus';
import SignIn from './views/SignIn';
import RequestAccess from './views/RequestAccess';
import Pending from './views/Pending';
import Review from './views/Review';
import Batch from './views/Batch';
import Members from './views/Members';
import Submitted from './views/Submitted';
import Lab, { type LabView } from './views/Lab';
import { useLabProgress } from './lib/labRun';
import BatchButton from './components/BatchButton';
import ProfileMenu from './components/ProfileMenu';
import Leaderboard from './components/Leaderboard';
import LiveUsers from './components/LiveUsers';

/**
 * Backstage has two areas, and the rail switches between them:
 *
 *   Translations — the review queues, batches and people, at / and /batch.
 *   AI Lab       — tools for the card generator, at /lab.
 *
 * Each area brings its own rail groups and top-bar actions; the profile menu
 * is the one thing shared.
 */
type Area = 'translations' | 'lab';
type TranslationsView = Queue | 'batch' | 'people' | 'submitted';

export default function App() {
  const { session, member, loading, refresh } = useSession();

  if (loading) return <div className="gate"><p className="lede">Loading…</p></div>;
  if (!session) return <SignIn />;
  if (!member) return <RequestAccess session={session} onDone={() => void refresh()} />;
  if (member.status !== 'active') return <Pending member={member} />;
  return <Signed member={member} />;
}

/** Everything behind the door: the languages, the cart, and whichever screen
 *  the URL names. The cart lives here so focus mode and the workspace fill
 *  the same one. */
function Signed({ member }: { member: Member }) {
  const isOwner = member.role === 'owner';
  // An owner sees every language, full stop. Keying this off an empty list
  // meant an owner who had named their own languages when requesting access
  // was quietly restricted to them.
  // Held in state because a reviewer can change it without a page reload.
  const [myLangs, setMyLangs] = useState<string[]>(member.languages);
  // Held here so a rename reaches the trigger, the panel and anything else
  // showing it, without waiting for a session refresh.
  const [myName, setMyNameLocal] = useState(member.display_name);
  const locales = useMemo(() => (isOwner ? [...LOCALES] : myLangs), [isOwner, myLangs]);
  const batch = useBatch(member.id);
  const route = useRoute();

  if (route.path === '/contribute') {
    return <Focus member={member} locales={locales} batch={batch} params={route.params} />;
  }
  // Every lab run is a paid generation, so the lab is for owners and the
  // people an owner has made AI editors.
  const canUseLab = isOwner || member.ai_editor;
  const area: Area = route.path.startsWith('/lab') && canUseLab ? 'lab' : 'translations';
  const labView: LabView = route.path === '/lab/scores' ? 'scores' : 'compare';
  return (
    <Workspace
      member={member} locales={locales} batch={batch}
      myName={myName} onRenamed={setMyNameLocal} onLanguages={setMyLangs}
      area={area} canUseLab={canUseLab} labView={labView}
      initialView={route.path === '/batch' ? 'batch' : 'flagged'}
    />
  );
}

function Workspace({
  member, locales, batch, myName, onRenamed, onLanguages, area, canUseLab, labView, initialView,
}: {
  member: Member;
  locales: string[];
  batch: ReturnType<typeof useBatch>;
  myName: string;
  onRenamed: (name: string) => void;
  onLanguages: (langs: string[]) => void;
  area: Area;
  canUseLab: boolean;
  labView: LabView;
  initialView: TranslationsView;
}) {
  const isOwner = member.role === 'owner';
  const { items, decisions, decide, undo, removeItem, clearBatch, error, setError } = batch;
  // A comparison keeps running while you are elsewhere; the rail says so.
  const labProgress = useLabProgress();

  const [view, setView] = useState<TranslationsView>(initialView);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [justSent, setJustSent] = useState(0);
  const [hideHow, setHideHow] = useState(false);
  // The rail is a drawer below 860px. Only reachable from the menu button,
  // which is hidden above that width, so it cannot be left open on a desktop.
  const [menuOpen, setMenuOpen] = useState(false);
  // Bumped alongside the queue counts, which is every point at which somebody's
  // standing can actually have moved: a batch sent, or a decision made.
  const [boardKey, setBoardKey] = useState(0);

  const reloadCounts = useCallback(async () => {
    if (!locales.length) return;
    const [queues, waiting] = await Promise.all([
      queueCounts(locales),
      isOwner ? countAwaitingDecision() : Promise.resolve(0),
    ]);
    setCounts({ ...queues, waiting });
    setBoardKey((k) => k + 1);
  }, [locales, isOwner]);

  useEffect(() => { void reloadCounts(); }, [reloadCounts]);

  // Escape closes the drawer, and the page underneath does not scroll while it
  // is open — on a phone that is the difference between a menu and a trap.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  const send = useCallback(async (note: string | null) => {
    setJustSent(await batch.send(note));
    void reloadCounts();
  }, [batch, reloadCounts]);

  const go = (v: TranslationsView) => {
    setJustSent(0); setView(v); setMenuOpen(false);
    // /batch is a real address so focus mode can hand back to it; every other
    // view lives at the root, where the app has always been.
    navigate(v === 'batch' ? '/batch' : '/', { replace: true });
  };

  const goArea = (a: Area) => {
    setMenuOpen(false);
    navigate(a === 'lab' ? '/lab' : '/');
  };

  return (
    <div className="app">
      {menuOpen && <div className="drawer-scrim" onClick={() => setMenuOpen(false)} />}
      <aside className={`rail${menuOpen ? ' open' : ''}`}>
        <div className="brand"><b>Couch</b><span>Backstage</span></div>

        {canUseLab && (
          <nav className="areas" aria-label="Area">
            <button className="area" aria-current={area === 'translations'} onClick={() => goArea('translations')}>
              <span className="area-ic" aria-hidden="true">文</span>
              <span className="area-t">Translations<span className="area-d">Decks, app and store copy</span></span>
            </button>
            <button className="area" aria-current={area === 'lab'} onClick={() => goArea('lab')}>
              <span className={`area-ic${labProgress ? ' busy' : ''}`} aria-hidden="true">✦</span>
              <span className="area-t">AI Lab
                <span className="area-d">
                  {labProgress
                    ? `Comparing… ${labProgress.done} of ${labProgress.total} models in`
                    : 'The card generator, examined'}
                </span>
              </span>
            </button>
          </nav>
        )}

        {area === 'translations' && (
          <>
            <div className="rail-grp">
              <div className="rail-h">Queues</div>
              <button className="nav" aria-current={view === 'flagged'} onClick={() => go('flagged')}>
                <span className="dot" style={{ background: 'var(--crit)' }} />
                Known problems<span className="count alert">{counts.flagged ?? '—'}</span>
              </button>
              <button className="nav" aria-current={view === 'unread'} onClick={() => go('unread')}>
                <span className="dot" style={{ background: 'var(--warn)' }} />
                Needs review<span className="count">{counts.unread ?? '—'}</span>
              </button>
              <button className="nav" aria-current={view === 'reviewed'} onClick={() => go('reviewed')}>
                <span className="dot" style={{ background: 'var(--ok)' }} />
                Already checked<span className="count">{counts.reviewed || '—'}</span>
              </button>
              <button className="nav" aria-current={view === 'batch'} onClick={() => go('batch')}>
                <span className="dot" style={{ background: 'var(--accent)' }} />
                My batch<span className="count">{items.length || '—'}</span>
              </button>
              {isOwner && (
                <>
                  <button className="nav" aria-current={view === 'submitted'} onClick={() => go('submitted')}>
                    <span className="dot" style={{ background: 'var(--ok)' }} />From reviewers
                    <span className={`count${counts.waiting ? ' alert' : ''}`}>
                      {counts.waiting || '—'}
                    </span>
                  </button>
                  <button className="nav" aria-current={view === 'people'} onClick={() => go('people')}>
                    <span className="dot" style={{ background: 'var(--faint)' }} />People
                  </button>
                </>
              )}
            </div>

            <div className="rail-grp">
              <div className="rail-h">Leaderboard</div>
              <Leaderboard meId={member.id} refreshKey={boardKey} />
            </div>

            <LiveUsers />
          </>
        )}

        {area === 'lab' && (
          <>
            <div className="rail-grp">
              <div className="rail-h">Tools</div>
              <button className="nav" aria-current={labView === 'compare'}
                onClick={() => { setMenuOpen(false); navigate('/lab'); }}>
                <span className="dot" style={{ background: 'var(--accent)' }} />
                Compare models
                {labProgress && <span className="count">{labProgress.done}/{labProgress.total}</span>}
              </button>
              <button className="nav" aria-current={labView === 'scores'}
                onClick={() => { setMenuOpen(false); navigate('/lab/scores'); }}>
                <span className="dot" style={{ background: 'var(--ok)' }} />
                Scoreboard
              </button>
            </div>
            <div className="rail-grp">
              <div className="rail-h">Coming later</div>
              <div className="rail-note">
                Once a few comparisons are in, this is where scoring, saved prompt sets and
                A/B runs against real players will live.
              </div>
            </div>
          </>
        )}
      </aside>

      <div className="col">
        <div className="top">
          <button
            className="menubtn" aria-label="Menu" aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <span className="top-area">{area === 'lab' ? 'AI Lab' : 'Translations'}</span>
          <div className="who">
            {area === 'translations' && locales.length > 0 && (
              <button className="btn contribute" onClick={() => navigate('/contribute')}
                title="One card at a time, keyboard first">
                <span aria-hidden="true">⚡</span> Contribute…
              </button>
            )}
            {area === 'translations' && <BatchButton count={items.length} onClick={() => go('batch')} />}
            <ProfileMenu
              member={member} name={myName} locales={locales}
              onLanguages={onLanguages} onRenamed={onRenamed}
            />
          </div>
        </div>

        <main>
          {error && (
            <div className="banner" style={{ background: 'var(--crit-soft)', borderColor: 'var(--crit-line)' }}>
              <span style={{ color: 'var(--crit)', fontWeight: 800 }}>!</span>
              <span><span className="t" style={{ color: 'var(--crit)' }}>Something went wrong</span>
                <span className="d">{error}</span></span>
              <button className="btn sm" style={{ marginLeft: 'auto' }} onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}

          {area === 'lab' && <Lab meId={member.id} view={labView} />}

          {area === 'translations' && (
            <>
              {!locales.length && view !== 'people' && (
                <div className="panel"><div className="empty">
                  <b>No languages assigned yet</b>
                  Ask Sam to add the languages you can review.
                </div></div>
              )}

              {view === 'people' && <Members />}

              {/* Pass the memoised callback, not an inline arrow: Submitted lists it
                  as a dependency of its loader, and a new function every render
                  would re-fetch in a loop. */}
              {view === 'submitted' && <Submitted meId={member.id} onChanged={reloadCounts} />}

              {view === 'batch' && (
                <Batch items={items} onRemove={removeItem} onClear={clearBatch} onSubmit={send}
                  justSent={justSent} onKeepGoing={() => go('flagged')} />
              )}

              {(view === 'flagged' || view === 'unread' || view === 'reviewed') && locales.length > 0 && (
                <Review locales={locales} queue={view} meId={member.id} decisions={decisions}
                  onDecide={decide} onUndo={undo} showHow={!hideHow} onHideHow={setHideHow} />
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
